import { Link } from "@tanstack/react-router";
import { ShieldOff } from "lucide-react";
import type { ComponentType, FC } from "react";

import { useCan } from "@/lib/auth/useCan";

/** Full-page fallback for a route the user lacks permission to view. */
export function NoAccess({ perm }: { perm: string }) {
  return (
    <div className="flex h-full min-h-[50vh] items-center justify-center">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-xl border bg-card p-8 text-center shadow-sm">
        <ShieldOff className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-lg font-semibold">No access</h1>
        <p className="text-sm text-muted-foreground">
          You don&apos;t have permission to view this page. Ask an admin if you
          think you should.
        </p>
        <p className="font-mono text-[11px] text-muted-foreground">requires {perm}</p>
        <Link
          to="/"
          className="mt-2 rounded-md bg-power px-4 py-1.5 text-sm font-semibold text-white hover:bg-power-hover"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

/**
 * Route-level permission guard. Wrap a page component when registering its
 * route so a direct URL hit without the permission renders NoAccess instead
 * of a broken page:
 *
 *   createRoute({ ..., component: guarded("renewals.read", RenewalsPage) })
 *
 * No loading flash: AppShell only renders the router Outlet after /api/me
 * resolves, so useCan always has the real permission set here.
 */
export function guarded<P extends object>(perm: string, Component: ComponentType<P>): FC<P> {
  function Guarded(props: P) {
    return useCan(perm) ? <Component {...props} /> : <NoAccess perm={perm} />;
  }
  Guarded.displayName = `Guarded(${Component.displayName ?? Component.name ?? "Page"})`;
  return Guarded;
}
